<template>
  <div class="runners-page">
    <div class="filter-bar">
      <div class="search-box">
        <input 
          type="text" 
          v-model="searchKey" 
          placeholder="搜索跑手姓名、手机号..."
          class="search-input"
          @keyup.enter="search"
        />
        <button @click="search" class="search-btn">搜索</button>
        <button @click="openAddModal" class="search-btn add-btn">+ 添加跑手</button>
      </div>
      <div class="filter-selects">
        <select v-model="onlineStatus" class="filter-select" @change="fetchRunners">
          <option value="">在线状态</option>
          <option value="1">在线</option>
          <option value="0">离线</option>
        </select>
        <select v-model="workStatus" class="filter-select" @change="fetchRunners">
          <option value="">工作状态</option>
          <option value="1">正在送单</option>
          <option value="0">空闲</option>
        </select>
        <select v-model="certStatus" class="filter-select" @change="fetchRunners">
          <option value="">认证状态</option>
          <option v-for="opt in certStatusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
        <select v-model="status" class="filter-select" @change="fetchRunners">
          <option value="">账号状态</option>
          <option value="1">禁用</option>
          <option value="0">正常</option>
        </select>
      </div>
    </div>

    <div class="table-container">
      <table class="runners-table">
        <thead>
          <tr>
            <th>跑手ID</th>
            <th>姓名</th>
            <th>手机号</th>
            <th>跑手类型</th>
            <th>服务小区</th>
            <th>评分</th>
            <th>完成订单</th>
            <th>在线状态</th>
            <th>工作状态</th>
            <th>进行中订单数</th>
            <th>认证状态</th>
            <th>跑手推广人</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="runner in runners" :key="runner.id">
            <td>{{ runner.id }}</td>
            <td>{{ runner.real_name }}</td>
            <td>{{ runner.phone }}</td>
            <td>{{ runner.runnerTypeName || '未设置' }}</td>
            <td>{{ runner.community || '未设置' }}</td>
            <td>⭐{{ runner.rating }}</td>
            <td>{{ runner.completedOrders }}</td>
            <td><span :class="['online-status', runner.onlineStatus === 1 ? 'online' : 'offline']">{{ runner.onlineStatus === 1 ? '在线' : '离线' }}</span></td>
            <td><span :class="'work-status ' + runner.workStatus">{{ runner.workStatus === 1 ? '正在送单' : '空闲' }}</span></td>
            <td>{{ runner.runningOrderCount != null ? runner.runningOrderCount : 0 }}</td>
            <td><span :class="'cert-status ' + runner.certStatus">{{ getCertStatusName(runner.certStatus) }}</span></td>
            <td>
              <router-link
                v-if="runner.referrers && runner.referrers.length"
                :to="`/runner-referrer/detail/${runner.referrers[0].referrerId}`"
                class="referrer-link"
              >{{ runner.referrers[0].referrerName }}</router-link>
              <span v-else class="no-referrer">未关联</span>
            </td>
            <td>
              <button @click="viewDetail(runner.id)" class="action-btn view">查看</button>
              <button v-if="runner.status === 0" @click="toggleRunnerStatus(runner)" class="action-btn disable">禁用</button>
              <button v-if="runner.status === 1" @click="toggleRunnerStatus(runner)" class="action-btn approve">启用</button>
            </td>
          </tr>
          <tr v-if="runners.length === 0">
            <td colspan="12" class="empty-tip">暂无数据</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="addModalVisible" class="modal-mask" @click.self="closeAddModal">
      <div class="modal-box">
        <div class="modal-header">
          <span>添加跑手</span>
          <button class="modal-close" @click="closeAddModal">×</button>
        </div>
        <div class="modal-body">
          <div class="search-box modal-search">
            <input
              v-model="candidateKeyword"
              type="text"
              class="search-input"
              placeholder="输入手机号搜索用户"
              @keyup.enter="searchCandidates"
            />
            <button @click="searchCandidates" class="search-btn">搜索</button>
          </div>
          <div class="candidate-list">
            <div
              v-for="user in candidateList"
              :key="user.id"
              class="candidate-item"
              :class="{ selected: selectedUserId === user.id, disabled: user.isRunner }"
              @click="!user.isRunner && (selectedUserId = user.id)"
            >
              <span class="candidate-name">{{ user.real_name || '未命名' }}</span>
              <span class="candidate-phone">{{ user.phone }}</span>
              <span v-if="user.isRunner" class="candidate-tag">已是跑手</span>
              <span v-else-if="selectedUserId === user.id" class="candidate-tag selected-tag">已选择</span>
            </div>
            <div v-if="!candidateSearching && candidateList.length === 0" class="candidate-empty">
              未找到匹配的用户
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn cancel" @click="closeAddModal">取消</button>
          <button class="modal-btn confirm" :disabled="addSubmitting || !selectedUserId" @click="submitAddRunner">
            {{ addSubmitting ? '提交中...' : '确定添加' }}
          </button>
        </div>
      </div>
    </div>

    <div class="pagination">
      <button :disabled="currentPage === 1" @click="prevPage" class="page-btn">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
      <button :disabled="currentPage === totalPages" @click="nextPage" class="page-btn">下一页</button>
    </div>
  </div>
</template>

<script>
import { getRunnerList, updateRunnerStatus, createRunner, searchRunnerCandidates } from '@/api/runners'

export default {
  name: 'Runners',
  data() {
      return {
        searchKey: '',
        status: '',
        certStatus: '',
        onlineStatus: '',
        workStatus: '',
        currentPage: 1,
        totalPages: 1,
        runners: [],
        addModalVisible: false,
        addSubmitting: false,
        candidateKeyword: '',
        candidateSearching: false,
        candidateList: [],
        selectedUserId: null,
        certStatusOptions: [
          { value: 'unassigned', label: '未签约' },
          { value: 6, label: '已签约' },
          { value: 7, label: '已拒绝' }
        ]
      }
    },
  mounted() {
    this.fetchRunners()
  },
  methods: {
    getCertStatusName(status) {
      const map = { 0: '待邀约', 1: '待面谈', 2: '待核验原件', 3: '待提交照片', 4: '待核验照片', 5: '待签约', 6: '已签约', 7: '已拒绝' }
      return map[status] || '未知'
    },
    async fetchRunners() {
      const params = {
        page: this.currentPage,
        limit: 20
      }
      if (this.searchKey) {
        params.keyword = this.searchKey
      }
      if (this.status !== '') {
        params.status = parseInt(this.status)
      }
      if (this.certStatus !== '') {
        if (this.certStatus === 'unassigned') {
          params.certStatusLt = 6
        } else {
          params.certStatus = parseInt(this.certStatus)
        }
      }
      if (this.onlineStatus !== '') {
        params.onlineStatus = parseInt(this.onlineStatus)
      }
      if (this.workStatus !== '') {
        params.workStatus = parseInt(this.workStatus)
      }

      try {
        const res = await getRunnerList(params)
        if (res.code === 0) {
          this.runners = res.data.list || []
          this.currentPage = res.data.currentPage || 1
          this.totalPages = res.data.totalPages || 1
        }
      } catch (error) {
      }
    },
    search() {
      this.currentPage = 1
      this.fetchRunners()
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.fetchRunners()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.fetchRunners()
      }
    },
    viewDetail(id) {
      this.$router.push(`/user/runners/detail/${id}`)
    },
    async toggleRunnerStatus(runner) {
      const newStatus = runner.status === 0 ? 1 : 0
      const actionText = newStatus === 1 ? '禁用' : '启用'
      if (confirm(`确定要${actionText}跑手 ${runner.real_name} 吗？`)) {
        try {
          const res = await updateRunnerStatus(runner.id, newStatus)
          if (res.code === 0) {
            alert(`${actionText}成功`)
            this.fetchRunners()
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },
    openAddModal() {
      this.candidateKeyword = ''
      this.candidateList = []
      this.selectedUserId = null
      this.addModalVisible = true
    },
    closeAddModal() {
      if (this.addSubmitting) return
      this.addModalVisible = false
    },
    async searchCandidates() {
      if (!this.candidateKeyword) {
        alert('请输入手机号')
        return
      }
      this.candidateSearching = true
      try {
        const res = await searchRunnerCandidates(this.candidateKeyword)
        if (res.code === 0) {
          this.candidateList = res.data || []
        } else {
          alert(res.message || '搜索失败')
        }
      } catch (error) {
        alert('搜索失败')
      } finally {
        this.candidateSearching = false
      }
    },
    async submitAddRunner() {
      if (!this.selectedUserId) {
        alert('请选择一个用户')
        return
      }
      this.addSubmitting = true
      try {
        const res = await createRunner({ userId: this.selectedUserId })
        if (res.code === 0) {
          alert('添加成功')
          this.addModalVisible = false
          this.fetchRunners()
        } else {
          alert(res.message || '添加失败')
        }
      } catch (error) {
        alert('添加失败')
      } finally {
        this.addSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.runners-page {
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.search-box {
  display: flex;
  gap: 10px;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 250px;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.filter-selects {
  display: flex;
  gap: 15px;
  align-items: center;
}

.filter-select {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
}

.runners-table {
  width: 100%;
  border-collapse: collapse;
}

.runners-table th, .runners-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.runners-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 40px;
}

.work-status {
  padding: 4px 0;
  border-radius: 20px;
  font-size: 12px;
}

.work-status.1 { background: #e8f5e9; color: #2ecc71; }
.work-status.0 { background: #f5f5f5; color: #666; }

.cert-status {
  padding: 4px 0;
  border-radius: 20px;
  font-size: 12px;
}

.cert-status.0 { background: #f5f5f5; color: #666; }
.cert-status.1 { background: #fff3e0; color: #f39c12; }
.cert-status.2 { background: #e8f5e9; color: #2ecc71; }
.cert-status.3 { background: #ffebee; color: #e74c3c; }

.online-status {
  padding: 4px 0;
  border-radius: 20px;
  font-size: 12px;
}

.online-status.online { background: #e8f5e9; color: #2ecc71; }
.online-status.offline { background: #ffebee; color: #e74c3c; }

.referrer-link {
  color: #3498db;
  cursor: pointer;
  text-decoration: underline;
}

.referrer-link:hover {
  color: #217dbb;
}

.no-referrer {
  color: #999;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.action-btn.view {
  background: #3498db;
  color: #fff;
}

.action-btn.approve {
  background: #2ecc71;
  color: #fff;
}

.action-btn.disable {
  background: #e74c3c;
  color: #fff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.add-btn {
  background: #2ecc71;
}

.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  width: 420px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(0,0,0,0.18);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.form-item label {
  width: 70px;
  font-size: 14px;
  color: #333;
}

.form-input {
  flex: 1;
  padding: 9px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid #eee;
}

.modal-btn {
  padding: 8px 18px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.modal-btn.cancel {
  background: #f0f0f0;
  color: #666;
}

.modal-btn.confirm {
  background: #2ecc71;
  color: #fff;
}

.modal-btn.confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-search {
  margin-bottom: 16px;
}

.candidate-list {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
}

.candidate-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #f2f2f2;
  cursor: pointer;
}

.candidate-item:last-child {
  border-bottom: none;
}

.candidate-item:hover:not(.disabled) {
  background: #f7fbff;
}

.candidate-item.selected {
  background: #e8f4ff;
}

.candidate-item.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.candidate-name {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.candidate-phone {
  font-size: 13px;
  color: #888;
}

.candidate-tag {
  margin-left: auto;
  font-size: 12px;
  color: #999;
}

.candidate-tag.selected-tag {
  color: #3498db;
  font-weight: 600;
}

.candidate-empty {
  padding: 30px;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>
